import { useCallback, useState } from "react";
import { FormattedMessage } from "react-intl";

import ActionItemCtas from "@components/ActionItemCtas";
import ActionIconButton from "@components/buttons/ActionIconButton";
import BasicCard from "@components/cards/BasicCard";
import styles from "@components/carePlan/CarePlanActionItem.module.css";
import ClientAvatar from "@components/ClientAvatar";
import ToggleCheckbox from "@components/fields/ToggleCheckbox";
import Popover from "@components/popovers/Popover";
import PopoverItem from "@components/popovers/PopoverItem";
import BodyCopy from "@components/typography/BodyCopy";
import InternalLink from "@components/typography/InternalLink";
import useDestroyCarePlanActionItem from "@hooks/mutations/useDestroyCarePlanActionItem";
import useMutationWithValidation from "@hooks/useMutationWithValidation";
import useMyExpert from "@hooks/useMyExpert";
import useOpener from "@hooks/useOpener";
import useSanitizeHtml from "@hooks/useSanitizeHtml";
import {
  CarePlanActionItemToggleCompletedDocument,
  CarePlanActionItemToggleCompletedMutation,
  CarePlanActionItemToggleCompletedMutationVariables,
  CarePlanActionItemFragment as CarePlanActionItemType,
  CarePlanActionItemUpdateDocument,
  CarePlanActionItemUpdateMutation,
  CarePlanActionItemUpdateMutationVariables,
  GetJourneyWithPlanQuery,
  GoalStatusEnum
} from "@typing/Generated";
import { carePlanActionItemHasChanges, medicationsBatched, providersBatched } from "@utils/modelUtils";
import { journeyRoute } from "@utils/routeUtils";

type Props = {
  actionItem: CarePlanActionItemType;
  journey: GetJourneyWithPlanQuery["journey"];
  onEdit: (actionItem: CarePlanActionItemType) => void;
};

const CarePlanActionItem = ({ actionItem, journey, onEdit }: Props) => {
  const [submitting, setSubmitting] = useState(false);
  const myExpert = useMyExpert();
  const popoverOpener = useOpener();

  const destroyCarePlanActionItem = useDestroyCarePlanActionItem(journey);

  const [carePlanActionItemToggleCompleted] = useMutationWithValidation<
    CarePlanActionItemToggleCompletedMutation,
    CarePlanActionItemToggleCompletedMutationVariables
  >(CarePlanActionItemToggleCompletedDocument, "carePlanActionItem");

  const [carePlanActionItemArchive] = useMutationWithValidation<
    CarePlanActionItemUpdateMutation,
    CarePlanActionItemUpdateMutationVariables
  >(CarePlanActionItemUpdateDocument, "carePlanActionItem", {
    update: store => {
      store.modify({
        fields: {
          archived() {
            return !actionItem.archived;
          }
        },
        id: store.identify({ ...actionItem })
      });
    }
  });

  const handleArchive = useCallback(() => {
    popoverOpener.close();
    carePlanActionItemArchive({
      archived: !actionItem.archived,
      id: actionItem.id
    });
  }, [actionItem.archived, actionItem.id, carePlanActionItemArchive, popoverOpener]);

  const handleEdit = useCallback(() => {
    popoverOpener.close();
    onEdit(actionItem);
  }, [actionItem, onEdit, popoverOpener]);

  const handleDestroy = useCallback(() => {
    popoverOpener.close();
    destroyCarePlanActionItem({
      id: actionItem.id
    });
  }, [actionItem.id, destroyCarePlanActionItem, popoverOpener]);

  const handleCompletedChange = useCallback(
    (completed: boolean) => {
      setSubmitting(true);
      carePlanActionItemToggleCompleted({
        completed,
        id: actionItem.id
      }).then(() => {
        setSubmitting(false);
      });
    },
    [actionItem.id, carePlanActionItemToggleCompleted]
  );

  const batchedMedications = medicationsBatched(journey.medications);
  const batchedProviders = providersBatched(journey.providers);
  const isArchived = actionItem.archived || actionItem.goal.archived;

  const sanitizedTitle = useSanitizeHtml(actionItem.title, journey.id, actionItem);

  const hasEditModeChanges =
    actionItem.goal.status === GoalStatusEnum.EDIT_MODE &&
    carePlanActionItemHasChanges(actionItem, journey.goals?.find(goal => goal.id === actionItem.goal.activeGoalId));

  return (
    <BasicCard
      border={hasEditModeChanges ? "danger-300" : undefined}
      hover="none"
      inactive={isArchived}
      padding="small"
    >
      <div className={`${styles.actionItem}`} data-test="care-plan-action-item">
        <div>
          <div className={styles.flex}>
            <ToggleCheckbox
              checked={actionItem.completed}
              disabled={submitting || actionItem.goal.hasDuplicateGoalInEditMode}
              name="actionItemComplete"
              onChange={handleCompletedChange}
              tabIndex={0}
            />
            <div>
              <BodyCopy color="gray">{sanitizedTitle}</BodyCopy>
            </div>
          </div>
        </div>
        <div>
          <div className={styles.flex}>
            <div className={styles.assignees}>
              {actionItem.assignments?.map((assignee, index) => (
                <ClientAvatar
                  clientId={assignee.id}
                  color={
                    index === 0
                      ? "primary-500"
                      : index === 1
                        ? "secondary-500"
                        : index === 2
                          ? "secondary-500"
                          : "primary"
                  }
                  firstName={assignee.firstName}
                  journeyId={journey.id}
                  key={assignee.id}
                  lastName={assignee.lastName}
                />
              ))}
            </div>
            <div className={styles.edit}>
              {myExpert ? (
                <Popover isOpen={popoverOpener.isOpen} opener={popoverOpener}>
                  <PopoverItem icon="edit" onClick={handleEdit} textKey="dictionary.edit" />
                  <PopoverItem icon="trash" noBorder={true} onClick={handleDestroy} textKey="dictionary.delete" />
                  <PopoverItem
                    icon="boxArchive"
                    noBorder={true}
                    onClick={handleArchive}
                    textKey={
                      actionItem.archived ? "pages.carePlan.base.goals.unarchive" : "pages.carePlan.base.goals.archive"
                    }
                  />
                </Popover>
              ) : (
                <ActionIconButton
                  ariaLabelKey="accessibility.ariaLabels.edit"
                  disabled={actionItem.goal.hasDuplicateGoalInEditMode}
                  icon="pencil"
                  onClick={() => {
                    handleEdit();
                  }}
                />
              )}
            </div>
          </div>
        </div>
      </div>
      <div className={styles.info}>
        {actionItem.worksheet?.id && (
          <div className={styles.additionalItems}>
            <InternalLink
              to={journeyRoute({
                journeyId: journey.id,
                linkAfterJourney: `/worksheets/${actionItem.worksheet.id}`,
                myExpert
              })}
            >
              <FormattedMessage id="pages.carePlan.openInteractiveDocument" />
            </InternalLink>
          </div>
        )}
        {actionItem.cta && (
          <div className={styles.additionalItems}>
            <ActionItemCtas
              actionItem={actionItem}
              batchedMedications={batchedMedications}
              batchedProviders={batchedProviders}
              journey={journey}
            />
          </div>
        )}
      </div>
    </BasicCard>
  );
};

export default CarePlanActionItem;
